import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useToast } from "../../client/hooks/use-toast";
import useTranslation from "../../client/hooks/useTranslation";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import { getAccountGroupsByUser, getPropFirmTemplates, getTradovateConnections, useQuery } from "wasp/client/operations";
import AddTradeModal from "../../trading-journal/AddTradeModal";
import ManualTradeDialog from "../../trading-journal/ManualTradeDialog";
import CsvImportDialog from "../../trading-journal/CsvImportDialog";
import { AddAccountsModal } from "../../trading-accounts/TradingAccountsPage";
import TradovateConnectWizard from "../../trading-accounts/tradovate/TradovateConnectWizard";
import TradovateImportReview from "../../trading-accounts/tradovate/TradovateImportReview";
import { normalizePropFirmTemplateId } from "../../trading-accounts/tradovate/tradovatePropFirmParam";
import { Dialog, DialogContent } from "../../client/components/ui/dialog";
import AddPayoutModal from "../../prop-firm-manager/AddPayoutModal";
import PlanSelectionModal from "../../dashboard/PlanSelectionModal";
import { userHasBrokerSync } from "../../payment/plans";
import type { EnrichedGroup, PropFirmTemplateSnapshot } from "../../trading-accounts/accountTypes";
import { useLiteData } from "../data/LiteDataProvider";

/**
 * Les actions du Lite ouvrent les VRAIES fenêtres de l'app (mêmes formulaires,
 * mêmes validations, mêmes limites de plan) puis rafraîchissent les données.
 */
interface LiteActions {
  openAddTrade: () => void;
  openManualTrade: (accountId?: string | null) => void;
  openImportCsv: () => void;
  openAddAccount: () => void;
  openConnectBroker: () => void;
  /** Ajouter un compte à une connexion Tradovate existante. */
  openAddToConnection: (connectionId: string) => void;
  /** Revue d'import Tradovate d'une connexion, prop firm pré-remplie si connue. */
  openTradovateImport: (connectionId: string, propFirmTemplateId: string | null) => void;
  openAddPayout: () => void;
  openUpgrade: () => void;
}

const Ctx = createContext<LiteActions | null>(null);

export function LiteActionsProvider({ children }: { children: ReactNode }) {
  const { data: user } = useAuth();
  const { refetchAll, rawAccounts, filters } = useLiteData();
  const [chooser, setChooser] = useState(false);
  const [manual, setManual] = useState<{ open: boolean; accountId?: string | null }>({ open: false });
  const [csv, setCsv] = useState(false);
  const [addAccount, setAddAccount] = useState(false);
  const [wizard, setWizard] = useState<{ open: boolean; connectionId?: string | null }>({ open: false });
  const [payout, setPayout] = useState(false);
  const [upgrade, setUpgrade] = useState(false);

  // Données du formulaire de compte : chargées seulement quand il s'ouvre.
  const { data: groups = [] } = useQuery(getAccountGroupsByUser, undefined, { enabled: addAccount });
  const { data: templates = [] } = useQuery(getPropFirmTemplates, undefined, { enabled: addAccount });
  // `{}` exactement : même entrée de cache que le reste de l'app.
  const { data: tradovateConnections = [] } = useQuery(getTradovateConnections, {});

  // Revue d'import Tradovate : même chemin par paramètres d'URL que la page
  // Advanced. C'est aussi là qu'atterrit le retour OAuth (?tradovateImport=…).
  const [searchParams, setSearchParams] = useSearchParams();
  const importConnectionId = searchParams.get("tradovateImport");
  const importPropFirmId = normalizePropFirmTemplateId(searchParams.get("propFirm"));
  const openImport = useCallback((connId: string, propFirmId: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tradovateImport", connId);
      if (propFirmId) next.set("propFirm", propFirmId); else next.delete("propFirm");
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  // Retour OAuth Tradovate en échec (?tradovateError=…) : même message que l'Advanced.
  const { toast } = useToast();
  const tt = useTranslation().tradovate;
  useEffect(() => {
    const oauthError = searchParams.get("tradovateError");
    if (!oauthError) return;
    toast({
      title: tt.oauthErrorTitle,
      description: oauthError === "plan_required" ? tt.oauthPlanRequiredDescription : tt.oauthErrorDescription,
      variant: "destructive",
    });
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("tradovateError");
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams, toast, tt]);
  const closeImport = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("tradovateImport");
      next.delete("propFirm");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const canUseMetaApi = !!user && userHasBrokerSync(user as any);

  const actions = useMemo<LiteActions>(() => ({
    openAddTrade: () => setChooser(true),
    openManualTrade: accountId => setManual({ open: true, accountId: accountId ?? filters.accountId }),
    openImportCsv: () => setCsv(true),
    openAddAccount: () => setAddAccount(true),
    openConnectBroker: () => setWizard({ open: true }),
    // Connexion déjà rattachée à une prop firm : directement la revue d'import,
    // sans redemander la prop firm (même règle que la page Advanced).
    openTradovateImport: (connId, propFirmId) => openImport(connId, propFirmId),
    openAddToConnection: connId => {
      const conn = (tradovateConnections as any[]).find(c => c.id === connId);
      const propFirmId = conn?.propFirmTemplate?.id ?? null;
      if (propFirmId) openImport(connId, propFirmId);
      else setWizard({ open: true, connectionId: connId });
    },
    openAddPayout: () => setPayout(true),
    openUpgrade: () => setUpgrade(true),
  }), [filters.accountId, tradovateConnections, openImport]);

  return (
    <Ctx.Provider value={actions}>
      {children}
      <AddTradeModal
        open={chooser}
        onOpenChange={setChooser}
        onSelectManual={() => setManual({ open: true, accountId: filters.accountId })}
        onImportDone={refetchAll}
        onImportCSV={() => setTimeout(() => setCsv(true), 0)}
      />
      <ManualTradeDialog
        open={manual.open}
        onOpenChange={open => setManual(m => ({ ...m, open }))}
        defaultAccountId={manual.accountId ?? null}
        onCreated={refetchAll}
      />
      <CsvImportDialog open={csv} onOpenChange={setCsv} onImported={refetchAll} />
      <AddAccountsModal
        open={addAccount}
        onClose={() => setAddAccount(false)}
        groups={groups as EnrichedGroup[]}
        templates={templates as PropFirmTemplateSnapshot[]}
        existingAccounts={rawAccounts as { name: string }[]}
        onSuccess={refetchAll}
        canUseMetaApi={canUseMetaApi}
        onUpgradeRequest={() => { setAddAccount(false); setUpgrade(true); }}
        tradovateConnections={tradovateConnections as any[]}
        onAddToConnection={connId => { setAddAccount(false); actions.openAddToConnection(connId); }}
        onConnectNewPropFirm={() => { setAddAccount(false); setWizard({ open: true }); }}
      />
      {wizard.open && (
        <TradovateConnectWizard
          open={wizard.open}
          onClose={() => { setWizard({ open: false }); refetchAll(); }}
          existingConnectionId={wizard.connectionId ?? undefined}
          onAddAccount={propFirmId => {
            const connId = wizard.connectionId;
            setWizard({ open: false });
            if (connId) openImport(connId, propFirmId);
          }}
        />
      )}
      <Dialog open={!!importConnectionId} onOpenChange={v => !v && closeImport()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {importConnectionId && (
            <TradovateImportReview
              connectionId={importConnectionId}
              propFirmTemplateId={importPropFirmId}
              onDone={() => { closeImport(); refetchAll(); }}
            />
          )}
        </DialogContent>
      </Dialog>
      <AddPayoutModal open={payout} onClose={() => setPayout(false)} accounts={rawAccounts as any} onSuccess={refetchAll} />
      <PlanSelectionModal open={upgrade} onOpenChange={setUpgrade} />
    </Ctx.Provider>
  );
}

export function useLiteActions() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLiteActions hors LiteActionsProvider");
  return v;
}
